import { UserAvailabilityStatus } from '@streem/sdk-core';
import appLogger from '../util/logging/app_logger';
import { fromStream, IStreamListener } from 'mobx-utils';
import { useEffect, useState } from 'react';
import { of, Observable } from 'rxjs';
import { mapApiStatusToWallStatus } from '../util/availability_status_utils';
import { useGlobalStore } from './use_global_context';

// This hook addresses an edge case where the `availabilityStatus` reads as OFFLINE after switching workspaces,
// even though an API call has set the status to NOT_AVAILABLE.
// To reproduce the issue, click on `Switch Workspaces` in EV2 and then return to the previous workspace.
// You may need to repeat this process several times, as the issue does not always occur.

export const useMatchUserAvailabilityStatus = (
    useApiValidation: boolean,
): IStreamListener<UserAvailabilityStatus> => {
    const { sdkStore, userStore } = useGlobalStore();
    const [status, setStatus] = useState<IStreamListener<UserAvailabilityStatus | undefined>>();

    useEffect(() => {
        if (!useApiValidation || !sdkStore.userAvailabilityStatus) return;

        if (sdkStore?.userAvailabilityStatus?.current) {
            appLogger.info('Within hook that accesses userStore and SdkStore');
            const apiStatus = mapApiStatusToWallStatus(userStore.availabilityStatus);
            if (apiStatus === sdkStore.userAvailabilityStatus.current.availabilityStatus) {
                setStatus(sdkStore.userAvailabilityStatus);
            } else {
                appLogger.debug(
                    'Encountered a mismatched status between the wall and the API, defaulting to NOT_AVAILABLE status',
                );
                // casting to UserAvailabilityStatus to match the return type of userAvailabilityStatus
                // this allows for a consistant return type from this hook.
                const defaultAvailabilityStatus = of({
                    availabilityStatus: 2,
                }) as Observable<UserAvailabilityStatus>;
                const defaultAvailabilityStatusStream = fromStream(defaultAvailabilityStatus);

                setStatus(defaultAvailabilityStatusStream);
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sdkStore.userAvailabilityStatus, useApiValidation, userStore.availabilityStatus]);

    if (!useApiValidation) {
        return sdkStore?.userAvailabilityStatus;
    }
    return status;
};
